/**
 * Plain-language success and error messages for school office staff.
 */

export const FEEDBACK = {
    studentAdded: "The student has been added to the class register. You can find them under All students.",
    classAdded: "The class has been added. You can now add subjects and enrol students into this class.",
    classesBulkAdded: (created, skipped) => {
        let msg = `Done. ${created} new class${created === 1 ? "" : "es"} added to your school.`;
        if (skipped) {
            msg += ` ${skipped} class${skipped === 1 ? " was" : "es were"} already in the system and ${skipped === 1 ? "was" : "were"} not added again.`;
        }
        return msg;
    },
    teacherAdded: "The teacher has been added. Give them their login email and password so they can sign in.",
    noticeAdded: "The notice has been posted. Students and teachers will see it on their dashboards.",
    subjectAdded: "Subject(s) saved for this class.",
    schoolRegistered:
        "Your school account is ready with a 1-month free trial. Open Step 1 in the menu to set up classes and teachers.",
    schoolRegisteredPendingVerification:
        "Registration submitted. A system administrator will verify your school before your free trial begins. You can sign in anytime to check approval status.",
    done: "Saved successfully.",
    saved: "Your changes have been saved.",
    published: "The school page is now live for parents to view.",
    unpublished: "The school page has been taken offline.",
    feeStructuresCreated: (n) =>
        `Fee setup complete for ${n} class${n === 1 ? "" : "es"}. New students in those classes will be billed automatically.`,
    feeStructuresAllExist:
        "Every class in this scope already has fees for this term. Open Fee setup to edit an existing structure, or choose another term.",
    paymentRecorded: "Payment recorded in the fees cash book.",
    loginCopied: "Login details copied. You can paste them into WhatsApp or email for the teacher.",
    approved: "Approved successfully.",
    rejected: "Marked as rejected.",
    deleted: "Removed successfully.",
    profileSaved: "Your profile has been updated.",
};

export const ERROR_HINTS = {
    network:
        "Could not reach the server. Check your internet connection, wait a moment, and try again. If this continues, contact your system supporter.",
    login:
        "Email or password is not correct. Check CAPS LOCK, try again, or ask your school IT person to reset your password.",
    required: "Please fill in all fields marked as required before saving.",
    generic: "Something went wrong. Read the message below, fix the issue, and try again.",
};

/** Turn API / axios errors into a short sentence staff can understand. */
export function formatAdminError(error, fallback = ERROR_HINTS.generic) {
    if (!error) return fallback;
    if (typeof error === "string") return error.trim() || fallback;

    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data?.message && String(data.message).trim()) return String(data.message).trim();

    if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        return ERROR_HINTS.network;
    }

    if (error.message && String(error.message).trim()) {
        const msg = String(error.message).trim();
        if (/missing authorization|supabase/i.test(msg)) {
            return "The system is not fully configured yet. Contact the person who set up Maphunziro.";
        }
        return msg;
    }

    return fallback;
}

/** Standard delay before redirect after success (time to read the message). */
export const SUCCESS_NAVIGATE_DELAY_MS = 3200;
